/**
 * Thin provider-agnostic image-analytics emitter.
 *
 * The web app has no client analytics pipeline; image events go through one
 * tiny Cloud Function (structured Cloud Logging line) so the Google-stack
 * tooling can query and alert on them. Emitting is strictly fire-and-forget:
 * an analytics hiccup must never fail or slow a customer's upload.
 *
 * Also hosts the session-aggregated variant-coverage counters for the serving
 * health metric: the variant resolver records one hit/fallback per render
 * in memory, and ONE `variant_coverage` event per surface flushes at
 * session end — never a per-render network call (renders are the hot path
 * at ~100k users).
 */
import { httpsCallable } from "firebase/functions";
import { firebaseFunctions } from "../store/firebaseApp";
import {
  IMAGE_EVENTS,
  IMAGE_EVENT_ALLOWED_PROPS,
  type ImageEventName,
  IMAGE_SURFACES,
  type ImageSurface,
} from "../constants/imageAnalyticsEvents";

type EventProps = Record<string, string | number | boolean>;

/** Drops any property not in the event's allowlist — PII can't ride along. */
function allowedProps(name: ImageEventName, props: EventProps): EventProps {
  const allowlist = IMAGE_EVENT_ALLOWED_PROPS[name] ?? [];
  const filtered: EventProps = {};
  for (const key of allowlist) {
    if (props[key] !== undefined) filtered[key] = props[key];
  }
  return filtered;
}

/**
 * Emits one image event. Fire-and-forget by design: failures are swallowed
 * (never surfaced to the user, never allowed to reject an upload flow).
 */
export function trackImageEvent(name: ImageEventName, props: EventProps = {}): void {
  try {
    const logImageEvent = httpsCallable(firebaseFunctions, "logImageEvent");
    void logImageEvent({ name, props: allowedProps(name, props) }).catch(() => {
      // Swallowed on purpose: an analytics failure must never surface.
    });
  } catch {
    // Analytics must never break the calling flow.
  }
}

/* ----- Variant-coverage session aggregation (consumed by the resolver) ---- */

type CoverageCounters = { hits: number; fallbacks: number };

const coverageBySurface = new Map<ImageSurface, CoverageCounters>();
let flushListenerRegistered = false;

function countersFor(surface: ImageSurface): CoverageCounters {
  let counters = coverageBySurface.get(surface);
  if (!counters) {
    counters = { hits: 0, fallbacks: 0 };
    coverageBySurface.set(surface, counters);
  }
  return counters;
}

function registerFlushOnSessionEnd(): void {
  if (flushListenerRegistered || typeof document === "undefined") return;
  flushListenerRegistered = true;
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushVariantCoverage();
  });
}

/** Records one variant-serving outcome. In-memory only — no network. */
export function recordVariantOutcome(surface: ImageSurface, outcome: "hit" | "fallback"): void {
  /* The ImageSurface union is erased at runtime and the event allowlist
   * filters KEYS, not values — a plain-JS caller could still pass a uid or
   * path as the label. Unknown labels are dropped (and never echoed: the
   * rejected value itself could be the PII). */
  if (!(IMAGE_SURFACES as readonly string[]).includes(surface)) {
    console.warn("recordVariantOutcome: unknown surface label dropped");
    return;
  }
  const counters = countersFor(surface);
  if (outcome === "hit") counters.hits += 1;
  else counters.fallbacks += 1;
  registerFlushOnSessionEnd();
}

/** Emits one aggregated coverage event per surface and resets the counters. */
export function flushVariantCoverage(): void {
  for (const [surface, { hits, fallbacks }] of coverageBySurface) {
    if (hits === 0 && fallbacks === 0) continue;
    trackImageEvent(IMAGE_EVENTS.variantCoverage, { surface, hits, fallbacks });
  }
  coverageBySurface.clear();
}
